import { computed } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import {
  getCustomerSavings,
  createCustomerSavings,
  updateCustomerSavings,
  deleteCustomerSavings,
} from '@/services/cpm/customerSavings.service'
import type { ICustomerSavingsCreationPayload, ICustomerSavingsUpdatePayload } from '@/types/cpm'
import type { ICustomerSavingsFilters } from '@/types/cpm/filters'
import type { Reactive } from 'vue'
import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'

const CUSTOMER_SAVINGS_QUERY_KEY = ['cpm', 'customer-savings']

export function useCustomerSavings() {
  const queryClient = useQueryClient()

  const getCustomerSavingsQuery = (filters: Reactive<ICustomerSavingsFilters>) => {
    return useQuery({
      queryKey: [...CUSTOMER_SAVINGS_QUERY_KEY, filters],
      queryFn: async () => getCustomerSavings(filters),
      enabled: computed(() => !!filters.site_id),
    })
  }

  const createCustomerSavingsMutation = useMutation({
    mutationFn: (payload: ICustomerSavingsCreationPayload) => createCustomerSavings(payload),
    onSuccess: () => {
      NotifySuccess('Savings record created successfully.')
      queryClient.invalidateQueries({ queryKey: CUSTOMER_SAVINGS_QUERY_KEY })
    },
    onError: NotifyErrorWrapper('Failed to create customer savings record'),
  })

  const updateCustomerSavingsMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ICustomerSavingsUpdatePayload }) =>
      updateCustomerSavings(id, payload),
    onSuccess: () => {
      NotifySuccess('Savings record updated successfully.')
      queryClient.invalidateQueries({ queryKey: CUSTOMER_SAVINGS_QUERY_KEY })
    },
    onError: NotifyErrorWrapper('Failed to update customer savings record'),
  })

  const deleteCustomerSavingsMutation = useMutation({
    mutationFn: (id: number) => deleteCustomerSavings(id),
    onSuccess: () => {
      NotifySuccess('Savings record deleted successfully.')
      queryClient.invalidateQueries({ queryKey: CUSTOMER_SAVINGS_QUERY_KEY })
    },
    onError: NotifyErrorWrapper('Failed to delete customer savings record'),
  })

  return {
    getCustomerSavingsQuery,
    createCustomerSavingsMutation,
    updateCustomerSavingsMutation,
    deleteCustomerSavingsMutation,
  }
}
